import { AlertTriangle, CheckCircle2, Globe2, Search, ShieldAlert, ShieldOff, UserMinus, WifiOff } from "lucide-react";


const statusStyle = {
  ready: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300",
  pending_dns: "border-amber-500/25 bg-amber-500/10 text-amber-300",
  ssl_issue: "border-rose-500/25 bg-rose-500/10 text-rose-300",
  not_routable: "border-slate-500/30 bg-slate-500/10 text-slate-300",
  flagged: "border-rose-500/40 bg-rose-500/15 text-rose-200",
};

const statusLabel = {
  ready: "DNS + TLS ready",
  pending_dns: "Pending DNS",
  ssl_issue: "SSL needs attention",
  not_routable: "Not routable",
  flagged: "Flagged",
};

const statusIcon = (readiness) => {
  switch (readiness) {
    case "ready": return <CheckCircle2 className="h-3 w-3" />;
    case "ssl_issue": return <ShieldAlert className="h-3 w-3" />;
    case "not_routable": return <WifiOff className="h-3 w-3" />;
    case "flagged": return <ShieldOff className="h-3 w-3" />;
    default: return <AlertTriangle className="h-3 w-3" />;
  }
};

export const AdminDomainTable = ({ domains, selected, onToggle, onToggleAll }) => {
  const allSelected = domains.length > 0 && domains.every((domain) => selected.has(domain.id));
  return (
    <div className="overflow-hidden border border-slate-800 rounded-lg" data-testid="admin-domain-table">
      <div className="hidden grid-cols-[44px_minmax(220px,1.6fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(145px,1fr)] gap-3 border-b border-slate-800 bg-slate-900/80 px-3 py-2 text-xs font-semibold uppercase text-slate-500 md:grid">
        <input type="checkbox" checked={allSelected} onChange={onToggleAll} className="h-4 w-4 accent-cyan-500" data-testid="domain-table-select-all" aria-label="Select visible domains" />
        <span>Managed domain</span><span>Assigned user</span><span>Server</span><span>Readiness</span>
      </div>
      <div className="divide-y divide-slate-800/80">
        {domains.map((domain) => {
          const checked = selected.has(domain.id);
          const slug = domain.domain.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
          return (
            <label key={domain.id} className={`grid cursor-pointer grid-cols-[32px_minmax(0,1fr)] gap-3 px-3 py-3 transition-colors hover:bg-slate-900/70 md:grid-cols-[32px_minmax(220px,1.6fr)_minmax(140px,1fr)_minmax(140px,1fr)_minmax(145px,1fr)] ${checked ? "bg-cyan-950/25" : "bg-slate-950/40"}`} data-testid={`domain-table-row-${slug}`}>
              <input type="checkbox" checked={checked} onChange={() => onToggle(domain.id)} className="mt-1 h-4 w-4 accent-cyan-500" data-testid={`domain-row-checkbox-${slug}`} />
              <div className="min-w-0">
                <p className="flex items-center gap-2 truncate text-sm font-medium text-slate-100"><Globe2 className="h-4 w-4 shrink-0 text-cyan-400" />{domain.domain}</p>
                {domain.fingerproxy_last_error && <p className="mt-1 line-clamp-2 text-xs text-slate-500" data-testid={`domain-error-${slug}`}>{domain.fingerproxy_last_error}</p>}
              </div>
              <div className="text-xs text-slate-300 md:pt-1" data-testid={`domain-user-${slug}`}>
                <span className="mr-2 text-slate-500 md:hidden">User:</span>
                {domain.assigned_users?.length ? domain.assigned_users.map((user) => user.username).join(", ") : <span className="inline-flex items-center gap-1 text-slate-500"><UserMinus className="h-3.5 w-3.5" />Unassigned</span>}
              </div>
              <div className="text-xs text-slate-300 md:pt-1" data-testid={`domain-server-${slug}`}><span className="mr-2 text-slate-500 md:hidden">Server:</span>{domain.server_name || "No server"}</div>
              <div className="md:pt-0.5">
                <span className={`inline-flex items-center gap-1 border px-2 py-1 text-xs rounded-md ${statusStyle[domain.readiness] || statusStyle.pending_dns}`} data-testid={`domain-readiness-${slug}`}>
                  {statusIcon(domain.readiness)}
                  {statusLabel[domain.readiness] || "Pending DNS"}
                </span>
              </div>
            </label>
          );
        })}
        {!domains.length && <div className="p-10 text-center text-sm text-slate-500" data-testid="domain-table-empty"><Search className="mx-auto mb-2 h-5 w-5" />No managed domains match these filters.</div>}
      </div>
    </div>
  );
};